/**
 Forced MFA setup view
 */

import React, {useState, useEffect} from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import {useApolloClient} from "@apollo/client";
import '../login-form/main.css';
import Logo from '../login-form/login-header.jpg';
import BackgroundImg from '../login-form/login-bg.jpg';
import Loading from "../../ui/loading";
import {submitMutation} from "../../http/mutate";
import {accountVar} from "../cache";
import SIGN_IN_GQL from '../sign-in.graphql';
import START_2FA_GQL from '../../user/settings/start-2fa.graphql';
import CONFIRM_2FA_GQL from '../../user/settings/confirm-2fa.graphql';

/**
 * Sign out and return to the login form
 */
const signOut = async (e) => {
    e.preventDefault();
    await firebase.auth().signOut();
    localStorage.clear();
    window.location.reload();
};

const MFASetup = () => {
    const client = useApolloClient();
    const [step, setStep] = useState('starting'); //starting | scanning | expired
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [secret, setSecret] = useState(null);
    const [token, setToken] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let _isMounted = true;
        (async () => {
            const {data} = await submitMutation(client, START_2FA_GQL, {}, setError, setIsProcessing);
            if(_isMounted && data)
            {
                setQrDataUrl(data.result.qrDataUrl);
                setSecret(data.result.secret);
                setStep('scanning');
            }
        })();
        return () => {
            _isMounted = false;
        };
    }, [client]);

    /**
     * Signs in with the given TOTP code to unlock the session
     */
    const completeSignIn = async (code) => {
        setIsProcessing(true);
        setError(null);
        let account = null;
        try {
            const firebaseToken = await firebase.auth().currentUser.getIdToken(true);
            const {error: signInError, data} = await submitMutation(client, SIGN_IN_GQL, {firebaseToken, twoFAToken: code});
            if(!!signInError)
            {
                //the TOTP window lapsed before the re-signIn: ask for a fresh code
                if(signInError === '2FA Required')
                {
                    setToken('');
                    setStep('expired');
                }
                else {
                    setError(signInError);
                }
            }
            else {
                account = data.result;
            }
        } catch (e) {
            setError('Something went wrong. Please try again.');
            console.error(`getIdToken error: ${e.message}`);
        }
        setIsProcessing(false);
        if(account)
        {
            localStorage.setItem('token', account.jwtToken);
            accountVar(account);
        }
    };

    /**
     * Confirms the TOTP setup, then unlocks the session
     */
    const onConfirmSetup = async () => {
        if(token.length !== 6) return;
        setIsProcessing(true);
        setError(null);
        const {error: confirmError} = await submitMutation(client, CONFIRM_2FA_GQL, {token, secret});
        if(!!confirmError)
        {
            setError(confirmError);
            setIsProcessing(false);
            return;
        }
        await completeSignIn(token);
    };

    /**
     * Retries the unlock with a fresh code after the TOTP window lapsed
     */
    const onRetry = async () => {
        if(token.length !== 6) return;
        await completeSignIn(token);
    };

    /**
     * When enter is pressed on input
     */
    const onEnter = (evt) => {
        if (evt.key === 'Enter') {
            evt.preventDefault();
            if(isProcessing) return;
            if(step === 'expired') {
                onRetry();
            } else {
                onConfirmSetup();
            }
        }
    };

    return (
        <div className="limiter">
            <div className="container-login100">
                <div className="wrap-login100">
                    <form className="login100-form validate-form">
                        <span className="login100-form-title" style={{paddingBottom: '43px'}}>
                            <img src={Logo} alt={'Saudi Tracking'}/>
                        </span>
                        <div className="text-center pb-3">
                            <h5 className="text-muted">Two-Factor Authentication</h5>
                            <p className="small text-muted">Your company requires two-factor authentication before you can continue.</p>
                        </div>
                        {
                            step === 'starting' && !error &&
                            <div className="text-center pb-3">
                                <Loading/>
                            </div>
                        }
                        {
                            step === 'scanning' &&
                            <>
                                <div className="text-center pb-3">
                                    <p className="small text-muted">Scan this QR code with your authenticator app:</p>
                                    <img src={qrDataUrl} alt="QR Code" style={{maxWidth: '200px'}}/>
                                    <p className="small text-muted">Or enter this code manually: <code>{secret}</code></p>
                                    <p className="small text-muted">Then enter the 6-digit code from your app below.</p>
                                </div>
                                <div className="wrap-input100">
                                    <input className={`input100 login-has-val`} type="text"
                                        autoFocus
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        onKeyDown={onEnter}
                                        maxLength={6}
                                        placeholder="000000"
                                    />
                                    <span className="focus-input100"/>
                                </div>
                            </>
                        }
                        {
                            step === 'expired' &&
                            <>
                                <div className="text-center pb-3">
                                    <p className="small text-muted">Your code expired. Enter a new 6-digit code from your authenticator app.</p>
                                </div>
                                <div className="wrap-input100">
                                    <input className={`input100 login-has-val`} type="text"
                                        autoFocus
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        onKeyDown={onEnter}
                                        maxLength={6}
                                        placeholder="000000"
                                    />
                                    <span className="focus-input100"/>
                                </div>
                            </>
                        }
                        <div className="login-flex-sb-m"
                             style={{paddingTop: '3px', paddingBottom: '32px', width: '100%'}}>
                            <div>
                                <a href="/" className="login-form-link" onClick={signOut}>
                                    Back to login
                                </a>
                            </div>
                        </div>
                        <div className="container-login100-form-btn">
                            {
                                step === 'scanning' &&
                                <button className="login100-form-btn" style={{fontSize: '18px'}}
                                        disabled={isProcessing}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onConfirmSetup();
                                        }}>
                                    {isProcessing && <Loading/>}
                                    {!isProcessing && <span>Confirm & Enable</span>}
                                </button>
                            }
                            {
                                step === 'expired' &&
                                <button className="login100-form-btn" style={{fontSize: '18px'}}
                                        disabled={isProcessing}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onRetry();
                                        }}>
                                    {isProcessing && <Loading/>}
                                    {!isProcessing && <span>Verify</span>}
                                </button>
                            }
                            {
                                !!error &&
                                <div className="invalid-feedback" style={{display: 'block'}}>
                                    {error}
                                </div>
                            }
                        </div>
                    </form>
                    <div className="login100-more" style={{backgroundImage: `url(${BackgroundImg})`}}/>
                </div>
            </div>
        </div>
    );
};

export default MFASetup;